import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { useAuth } from '../context/AuthContext';
import { login as apiLogin, initAdmin } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeSlash, Warning, User, Lock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_cc91a7b0-9aa5-4ba1-a9e7-50b1811a744d/artifacts/w3xcvyw6_escudo%20sf%207x8.png";

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [particlesInit, setParticlesInit] = useState(false);
  
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setParticlesInit(true);
    });
  }, []);

  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      toast.warning('Tu sesión ha expirado por inactividad');
    }
  }, [searchParams]);

  // Initialize admin on first load
  useEffect(() => {
    const initializeAdmin = async () => {
      try {
        const response = await initAdmin();
        if (response.data.password) {
          toast.info(`Usuario admin creado: ${response.data.username} / ${response.data.password}`);
        }
      } catch (error) {
        // Admin user already exists or service unavailable; silently ignore but log for debugging
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Admin initialization skipped:', error?.message);
        }
      }
    };
    initializeAdmin();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiLogin(username, password);
      loginUser(response.data.usuario, response.data.token);
      toast.success('Bienvenido al sistema');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const particlesOptions = {
    background: {
      color: { value: "transparent" },
    },
    fpsLimit: 60,
    particles: {
      color: { value: "#722F37" },
      links: {
        color: "#722F37",
        distance: 150,
        enable: true,
        opacity: 0.2,
        width: 1,
      },
      move: {
        enable: true,
        speed: 1,
        direction: "none",
        outModes: { default: "bounce" },
      },
      number: {
        density: { enable: true, area: 800 },
        value: 50,
      },
      opacity: { value: 0.3 },
      shape: { type: "circle" },
      size: { value: { min: 1, max: 3 } },
    },
    detectRetina: true,
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 relative overflow-hidden">
      {/* Particles background */}
      {particlesInit && (
        <div className="absolute inset-0 z-0">
          <Particles id="tsparticles" options={particlesOptions} />
        </div>
      )}

      {/* Login card */}
      <Card className="w-full max-w-md mx-4 relative z-10 backdrop-blur-lg bg-white/95 dark:bg-slate-800/95 shadow-xl border-0" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img 
              src={LOGO_URL} 
              alt="Escudo Secundaria Benito Juárez" 
              className="w-24 h-24 object-contain"
              data-testid="school-logo"
            />
          </div>
          <CardTitle className="font-heading text-2xl text-primary">
            Secundaria Benito Juárez
          </CardTitle>
          <CardDescription className="text-base">
            Sistema de Gestión Escolar
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm animate-fade-in" data-testid="login-error">
                <Warning size={18} weight="fill" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium">Usuario</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="username"
                  type="text"
                  placeholder="Ingresa tu usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="username-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full font-medium"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" />
                  Iniciando sesión...
                </span>
              ) : (
                'Iniciar sesión'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            <p>Sec. Foránea Mixta No. 7</p>
            <p>Ocotlán, Jalisco</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
